import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiSearch } from 'react-icons/fi';
import { FaRegCalendarAlt } from 'react-icons/fa';
import BackgroundColor from './BackgroundColor';
import { useDispatch, useSelector } from 'react-redux';
import { getAllBlogs } from '../Redux/Slice/blog.slice';
import BlogSkeleton from './skeleton-loaders/BlogSkeleton';

export default function Blog() {

    const dispatch = useDispatch();
    const allBlogs = useSelector((state) => state.blog.blogs);
    const loading = useSelector((state) => state.blog.loading);
    const [singleBlog, setSingleBlog] = useState(null);

    useEffect(() => {
        document.title = 'Blog • YOYO';
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (allBlogs.length > 0) {
            setSingleBlog(allBlogs[0]);
        }
    }, [allBlogs]);

    const [index, setIndex] = useState(0);
    const [expanded, setExpanded] = useState(false);
    const [query, setQuery] = useState('');

    useEffect(() => {
        dispatch(getAllBlogs())
    }, [dispatch])

    useEffect(() => {
        setIndex(0);
        setExpanded(false);
    }, [singleBlog]);

    const title = singleBlog?.title;
    const longText = singleBlog?.content;
    const isLongText = longText && longText.length > 300;
    const images = singleBlog?.blog_images?.map((image) => image?.url);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0'); // Get day and pad with zero if needed
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = monthNames[date.getMonth()]; // Get month name
        const year = date.getFullYear(); // Get full year
        return `${day}, ${month}, ${year}`; // Return formatted date
    };

    const author = { name: singleBlog?.author, date: formatDate(singleBlog?.createdAt) };

    const prev = () => setIndex((p) => (p - 1 + images.length) % images.length);
    const next = () => setIndex((p) => (p + 1) % images.length);

    // Function to handle blog selection
    const handleBlogSelect = (blog) => {
        setSingleBlog(blog);
    };

    const topArticles = allBlogs?.filter((blog) => blog._id !== singleBlog?._id) || [];

    const filteredTop = topArticles?.filter((a) => {
        const titleMatch = a.title.toLowerCase().includes(query.toLowerCase());
        const dateMatch = formatDate(a.createdAt).toLowerCase().includes(query.toLowerCase());
        return titleMatch || dateMatch;
    });

    if (loading || !allBlogs || allBlogs.length === 0) {
        return (
            <BackgroundColor>
                <BlogSkeleton />
            </BackgroundColor>
        );
    }

    return (
        <BackgroundColor>
            <section className="pt-24 pb-16">
                <div className="mx-auto w-[95%] sm:w-[92%] md:w-[90%] lg:max-w-[80%]">
                    <motion.section
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <h1 className="text-3xl md:text-5xl font-extrabold text-white">Latest Fresh From the <span className='gradient-text'>Gameverse</span> </h1>

                        <p className="text-gray-400 my-6">
                            Stay updated with the latest gaming news, trends, and guides from Yoyo Game Store.
                        </p>
                    </motion.section>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <motion.div
                            initial={{ opacity: 0, x: -16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="lg:col-span-2"
                        >
                            <div className="relative overflow-hidden rounded-xl border border-white/10 bg-[#121212] shadow-2xl">
                                {/* Hero slider */}
                                <div className="relative">
                                    <div className="aspect-[21/9] w-full overflow-hidden">
                                        <AnimatePresence mode="wait" initial={false}>
                                            <motion.img
                                                key={`${singleBlog?.id || 'default'}-${index}`}
                                                src={images?.[index]}
                                                alt={title}
                                                initial={{ opacity: 0.4, scale: 1.02 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0.2, scale: 1.02 }}
                                                transition={{ duration: 0.5 }}
                                                className="w-full h-full object-cover"
                                            />
                                        </AnimatePresence>
                                    </div>

                                    {/* Arrows */}
                                    {images?.length > 1 && (
                                        <>
                                            <button
                                                aria-label="Previous image"
                                                onClick={prev}
                                                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-white hover:bg-black/70 transition"
                                            >
                                                <FiChevronLeft size={22} />
                                            </button>
                                            <button
                                                aria-label="Next image"
                                                onClick={next}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-white hover:bg-black/70 transition"
                                            >
                                                <FiChevronRight size={22} />
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="px-6 md:px-10 pb-8 pt-6">
                                    <h1 className="font-serif text-lg md:text-3xl text-white tracking-wide">
                                        {title}
                                    </h1>

                                    <div className={`mt-4 text-sm md:text-[15px] leading-7 text-white/85 whitespace-pre-wrap ${expanded ? '' : 'line-clamp-3'}`}>
                                        {longText}
                                    </div>
                                    {isLongText && (
                                        <button
                                            onClick={() => setExpanded((v) => !v)}
                                            className="mt-2 text-blue-400 hover:text-blue-300 text-sm"
                                        >
                                            {expanded ? 'Show Less' : 'Show More'}
                                        </button>
                                    )}

                                    {/* Meta */}
                                    <div className="mt-6 flex items-center gap-3 text-white/80">
                                        <div className="min-w-8 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white font-semibold text-base">
                                            {author.name?.[0].toUpperCase()}
                                        </div>
                                        <div className="text-xs md:text-sm">
                                            <div>by <span className='capitalize'>{author.name}</span></div>
                                        </div>
                                        <div className="mx-2 h-4 w-px bg-white/20" />
                                        <div className="flex items-center gap-1 whitespace-nowrap text-xs md:text-sm">
                                            <FaRegCalendarAlt className="text-white/70" />
                                            <span>{author.date}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Sidebar */}
                        <motion.div
                            initial={{ opacity: 0, x: 16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.15 }}
                        >
                            <aside className="lg:sticky lg:top-28 h-fit space-y-6">
                                <div className="rounded-xl border border-white/10 bg-[#1111115d] p-4">
                                    <h3 className="text-lg font-semibold mb-3">Blog Search</h3>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            placeholder="Search"
                                            className="w-full rounded-md bg-black/50 border border-white/10 pl-3 pr-10 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500"
                                        />
                                        <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60" />
                                    </div>
                                </div>

                                <div className="rounded-xl border border-white/10 bg-[#1111115d] p-4">
                                    <h3 className="text-lg font-semibold mb-3">Other Blog</h3>
                                    <div className="divide-y divide-white/10 max-h-[48.5vh] overflow-x-hidden overflow-y-auto">
                                        {filteredTop.length === 0 && (
                                            <div className="py-3 flex items-center gap-3">
                                                <p className="text-white/60 text-sm">No blogs found</p>
                                            </div>
                                        )}
                                        {filteredTop.map((blog, blogIndex) => (
                                            <div
                                                key={blogIndex}
                                                className="py-3 flex items-center gap-3 hover:bg-white/5 rounded-lg px-2 -mx-2 transition-colors"
                                                onClick={() => handleBlogSelect(blog)}
                                            >
                                                <img
                                                    src={blog.blog_images?.[0]?.url}
                                                    alt={blog.title}
                                                    className="w-12 h-12 rounded object-cover flex-shrink-0"
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-medium leading-snug text-white hover:text-purple-300 transition-colors truncate">
                                                        {blog.title}
                                                    </div>
                                                    <div className="text-xs text-white/60 mt-1">
                                                        {formatDate(blog.createdAt)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </aside>
                        </motion.div>
                    </div>
                </div>
            </section>
        </BackgroundColor>
    );
}